import qs from "webopenfather-qs"; 
import { authManager } from './authManager';
import { globalAlert } from './globalAlert';

// 系统管理模块使用不同的路径前缀
const baseURL = "/translate/system";

export const systemRequest = <T>(
  path: string,
  options: REQUEST.Options
): Promise<T> => {
  const { params = {}, method = "GET", headers = {}, body = null, skipAuth = false, signal } = options;

  let temp = "";
  if (Object.keys(params).length) {
    temp = "?" + qs.stringify(params);
  }

  // 获取token
  const token = localStorage.getItem("token");
  
  // 构建请求头
  const requestHeaders: Record<string, string> = {
    ...headers,
  };
  
  // 如果有token且不需要跳过认证，添加到Authorization头
  if (token && !skipAuth) {
    // 清理token（去除前后空格和换行符）
    const cleanToken = token.trim();
    
    // 检查token是否已经包含"Bearer "前缀
    if (cleanToken.startsWith('Bearer ')) {
      requestHeaders["Authorization"] = cleanToken;
    } else {
      requestHeaders["Authorization"] = `Bearer ${cleanToken}`;
    }
  }
  
  // 判断body类型，设置相应的Content-Type和body
  let requestBody: BodyInit | null = null;
  if (body instanceof FormData) {
    // 如果是FormData，不设置Content-Type，让浏览器自动设置
    delete requestHeaders["content-type"];
    requestBody = body;
  } else if (body && typeof body === 'object') {
    // 检查是否包含File对象
    const hasFile = Object.values(body).some(value => value instanceof File);
    
    if (hasFile) {
      // 如果包含File对象，转换为FormData
      const formData = new FormData();
      Object.entries(body).forEach(([key, value]) => {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'boolean') {
          formData.append(key, value.toString());
        } else {
          formData.append(key, String(value));
        }
      });
      delete requestHeaders["content-type"];
      requestBody = formData;
    } else {
      // 如果是普通对象，设置为JSON
      requestHeaders["content-type"] = "application/json";
      requestBody = JSON.stringify(body);
    }
  }

  const url = baseURL + path + temp;
  
  return window
    .fetch(url, {
      method,
      headers: requestHeaders,
      body: requestBody,
      signal, // 添加signal支持
    })
    .then((res) => {
      if (!res.ok) {
        // 检查是否是token过期
        if (authManager.isTokenExpiredResponse(res.status)) {
          authManager.handleTokenExpired();
          const error = new Error('登录验证失效，请重新登录');
          (error as any).status = res.status;
          (error as any).isTokenExpired = true;
          throw error;
        }

        // 尝试解析响应体获取错误信息
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return res.json().then(data => {
            // 统一判断 code 字段：code === 0 表示成功，code !== 0 表示失败
            // 即使 HTTP 状态码不是 200，如果响应体中有 code 字段，也要判断并显示提示
            if (data.code !== undefined && data.code !== null && data.code !== 0 && data.code !== '0') {
              // 检查是否是token过期
              if (authManager.isTokenExpiredResponse(data.code, data)) {
                authManager.handleTokenExpired();
                const error = new Error('登录验证失效，请重新登录');
                (error as any).status = data.code;
                (error as any).isTokenExpired = true;
                throw error;
              }

              // 显示错误提示（使用 msg 字段）
              const errorMessage = data.msg || data.error || data.message || data.info || data.detail || '操作失败';
              // 确保显示错误提示
              if (errorMessage) {
                globalAlert.showError(errorMessage);
              }
            }
            
            // 优先使用error字段，如果没有则使用其他字段，最后才使用默认的错误信息
            const errorMessage = data.error || data.message || data.msg || data.info || data.detail || `请求失败 (${method} ${path})`;
            const error = new Error(errorMessage);
            (error as any).status = res.status;
            (error as any).response = data; // 保存完整的响应数据
            (error as any).msg = data.msg;
            throw error;
          });
        } else {
          // 如果不是JSON，尝试解析为文本
          return res.text().then(text => {
            try {
              const data = JSON.parse(text);
              const errorMessage = data.error || data.message || data.msg || data.info || data.detail || `请求失败 (${method} ${path})`;
              const error = new Error(errorMessage);
              (error as any).status = res.status;
              (error as any).response = data;
              throw error;
            } catch (e) {
              // 如果无法解析为JSON，使用文本内容
              const errorMessage = text || `请求失败 (${method} ${path})`;
              const error = new Error(errorMessage);
              (error as any).status = res.status;
              (error as any).response = { detail: text };
              throw error;
            }
          });
        }
      }
      
      // 检查响应内容类型
      const contentType = res.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        return res.json().then(data => {
          // 如果数据为null，直接返回
          if (data === null) {
            return data;
          }
          
          // 统一判断 code 字段：code === 0 表示成功，code !== 0 表示失败
          // 处理 code 可能是数字或字符串的情况
          if (data.code !== undefined && data.code !== null && data.code !== 0 && data.code !== '0') {
            // 检查是否是token过期
            if (authManager.isTokenExpiredResponse(data.code, data)) {
              authManager.handleTokenExpired();
              const error = new Error('登录验证失效，请重新登录');
              (error as any).status = data.code;
              (error as any).businessStatus = data.status;
              (error as any).isTokenExpired = true;
              throw error;
            }

            // 显示错误提示（使用 msg 字段）
            const errorMessage = data.msg || data.error || data.message || data.info || '操作失败';
            // 确保显示错误提示
            if (errorMessage) {
              globalAlert.showError(errorMessage);
            }
            
            const error = new Error(errorMessage);
            (error as any).status = data.code;
            (error as any).businessStatus = data.status;
            // 保存原始的error字段内容，供后续使用
            (error as any).error = data.error;
            // 保存 msg 字段，供后续使用
            (error as any).msg = data.msg;
            (error as any).response = { data, msg: data.msg };
            throw error;
          }
          
          // 检查业务状态码（兼容旧逻辑）
          if (data.status === 'fail' || data.code >= 400) {
            // 检查是否是token过期
            if (authManager.isTokenExpiredResponse(data.code, data)) {
              authManager.handleTokenExpired();
              const error = new Error('登录验证失效，请重新登录');
              (error as any).status = data.code;
              (error as any).businessStatus = data.status;
              (error as any).isTokenExpired = true;
              throw error;
            }

            // 优先使用error字段，如果没有则使用其他字段，最后才使用默认的'操作失败'
            const errorMessage = data.error || data.message || data.msg || data.info || '操作失败';
            globalAlert.showError(data.msg || errorMessage);
            
            const error = new Error(errorMessage);
            (error as any).status = data.code;
            (error as any).businessStatus = data.status;
            // 保存原始的error字段内容，供后续使用
            (error as any).error = data.error;
            // 保存 msg 字段，供后续使用
            (error as any).msg = data.msg;
            (error as any).response = { data, msg: data.msg };
            throw error;
          }
          return data;
        });
      } else {
        // 如果不是JSON，尝试解析为文本
        return res.text().then(text => {
          try {
            const data = JSON.parse(text);
            
            // 统一判断 code 字段：code === 0 表示成功，code !== 0 表示失败
            // 处理 code 可能是数字或字符串的情况
            if (data.code !== undefined && data.code !== null && data.code !== 0 && data.code !== '0') {
              // 检查是否是token过期
              if (authManager.isTokenExpiredResponse(data.code, data)) {
                authManager.handleTokenExpired();
                const error = new Error('登录验证失效，请重新登录');
                (error as any).status = data.code;
                (error as any).businessStatus = data.status;
                (error as any).isTokenExpired = true;
                throw error;
              }

              // 显示错误提示（使用 msg 字段）
              const errorMessage = data.msg || data.error || data.message || data.info || '操作失败';
              globalAlert.showError(errorMessage);
              
              const error = new Error(errorMessage);
              (error as any).status = data.code;
              (error as any).businessStatus = data.status;
              // 保存原始的error字段内容，供后续使用
              (error as any).error = data.error;
              // 保存 msg 字段，供后续使用
              (error as any).msg = data.msg;
              (error as any).response = { data, msg: data.msg };
              throw error;
            }
            
            // 检查业务状态码（兼容旧逻辑）
            if (data.status === 'fail' || data.code >= 400) {
              // 检查是否是token过期
              if (authManager.isTokenExpiredResponse(data.code, data)) {
                authManager.handleTokenExpired();
                const error = new Error('登录验证失效，请重新登录');
                (error as any).status = data.code;
                (error as any).businessStatus = data.status;
                (error as any).isTokenExpired = true;
                throw error;
              }

              // 优先使用error字段，如果没有则使用其他字段，最后才使用默认的'操作失败'
              const errorMessage = data.error || data.message || data.msg || data.info || '操作失败';
              globalAlert.showError(data.msg || errorMessage);
              
              const error = new Error(errorMessage);
              (error as any).status = data.code;
              (error as any).businessStatus = data.status;
              // 保存原始的error字段内容，供后续使用
              (error as any).error = data.error;
              // 保存 msg 字段，供后续使用
              (error as any).msg = data.msg;
              (error as any).response = { data, msg: data.msg };
              throw error;
            }
            return data;
          } catch (e) {
            return { message: text };
          }
        });
      }
    })
    .catch((error) => {
      // 如果是网络错误，提供更详细的错误信息
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        const networkError = new Error('网络连接失败，请检查网络连接或服务器状态');
        (networkError as any).status = 'NETWORK_ERROR';
        throw networkError;
      }
      
      throw error;
    });
};




